<i18n>
[
    "product__msrp--label",
    "reviews__reviews--label",
    "global__by",
]
</i18n>

<template>
    <div>
        <div class="c-product-info-header__rating-price-container u-spacer--1">
            <UrbnModalLauncher
                :disabled="reviewCount === 0 || undefined"
                :modalOptions="pdpReviewsViewAllTopModal"
                type="link"
                :class="{
                    'c-product-info-header__rating-star--disabled': reviewCount === 0,
                    'o-flex-vertical-center': true,
                }"
            >
                <AppIcon
                    v-for="index in MAX_RATING"
                    :key="`pdp-rating-stars-${index}`"
                    :icon="stars[index -1].icon"
                    :size="'16px'"
                    class="c-product-info-header__rating-star"
                    :color="stars[index -1].color"
                />
                <span class="c-product-info-header__review-count o-text--caption u-text--gray">
                    {{ $tc('reviews__reviews--label', reviewCount) }}
                </span>
            </UrbnModalLauncher>
            <ClosetBadge
                :loggedIn="loggedIn"
                :initialIsActive="product.inCloset"
                :isSmall="smallClosetButton || $mediaQueries.isSmallish"
                :choiceId="product.choiceId"
                :product="product"
                :productAnalyticsData="productAnalyticsData"
                :showInProductTour="showInProductTour"
                :hidePadding="true"
                :toastOptions="{ images: toastImages, description: toastDescription }"
                :popoverOffsets="popoverOffsets"
                :catalogResponseId="catalogResponseId"
                class="c-product-info-header__closet-badge"
            />
        </div>
        <h1 class="o-text--heading-2 u-spacer--0pt5">
            {{ productDetails.displayName }}
        </h1>
        <div v-if="brandUrls.length > 0" class="o-text--caption">
            <BaseLink
                v-if="brandUrls.length === 1"
                :to="{
                    name: brandRoute,
                    params: {
                        slug: brandUrls[0].slug,
                    }
                }"
                class="o-text--link"
            >
                {{ brandUrls[0].displayName }}
            </BaseLink>
            <template v-else>
                <template v-if="subBrandName">
                    <BaseLink
                        :to="{
                            name: brandRoute,
                            params: {
                                slug: subBrandName.slug,
                            }
                        }"
                        class="o-text--link"
                    >
                        {{ subBrandName.displayName }}
                    </BaseLink>
                    <template v-if="brandUrls.length > 1 && brandName">
                        {{ ` ${$t('global__by')}` }}
                        <BaseLink
                            :to="{
                                name: brandRoute,
                                params: {
                                    slug: brandName.slug
                                }
                            }"
                            class="o-text--link"
                        >
                            {{ brandName.displayName }}
                        </BaseLink>
                    </template>
                </template>
            </template>
            <span
                v-if="!!msrpValue && !isBuyMode"
                class="c-product-info-header__msrp u-text--gray"
            >
                {{ `${$t('product__msrp--label')} ${msrpValue}` }}
            </span>
        </div>
    </div>
</template>

<script>
import { get, isEmpty } from 'lodash-es';
import { mapGetters } from 'vuex';

import { MAX_RATING } from '~coreModules/core/js/product-constants';
import { URBN_BRANDS } from '~coreModules/core/js/constants';
import { MODALS } from '~modules/modals/js/modal-constants';
import { catalogPageNames } from '~modules/core/js/router-constants';
import { getProductModuleName } from '~coreRoutes/product/js/product-utils';
import { mapInstanceGetters, mapInstanceState } from '~coreModules/core/js/store-utils';

import { PRODUCT_TOUR_MODULE_NAME } from '~modules/product-tour/js/product-tour-store';
import { PRODUCT_TOUR_NAMES } from '~modules/product-tour/js/product-tour-constants';

import UrbnModalLauncher from '~coreModules/modals/components/UrbnModalLauncher.vue';
import ClosetBadge from '~routes/closet/components/ClosetBadge.vue';

export default {
    name: 'ProductInfoHeader',
    components: {
        UrbnModalLauncher,
        ClosetBadge,
    },
    props: {
        productDetails: {
            type: Object,
            required: true,
        },
        product: {
            type: Object,
            required: true,
        },
        smallClosetButton: {
            type: Boolean,
            default: true,
        },
        isBuyMode: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        MAX_RATING,
        brandRoute: catalogPageNames.brand,
        pdpReviewsViewAllTopModal: MODALS.pdpReviewsDrawer,
    }),
    computed: {
        ...mapGetters([
            'loggedIn',
        ]),
        ...mapInstanceState(getProductModuleName, {
            catalogResponseId: state => state.catalogResponseId,
        }),
        ...mapInstanceGetters(getProductModuleName, [
            'productAnalyticsData',
        ]),
        ...mapGetters(PRODUCT_TOUR_MODULE_NAME, [
            'shouldRenderGivenTour',
        ]),
        showInProductTour() {
            return this.shouldRenderGivenTour(PRODUCT_TOUR_NAMES.closet);
        },
        brandUrls() {
            return get(this.productDetails, 'brandUrls', []);
        },
        firstProductImage() {
            return get(this.product, 'imageUrls.0', '');
        },
        subBrandName() {
            if (isEmpty(this.brandUrls)) return false;
            const filteredBrandUrls = this.brandUrls.filter(brand => !URBN_BRANDS.includes(brand.displayName));
            if (isEmpty(filteredBrandUrls)) return false;
            return filteredBrandUrls[0];
        },
        brandName() {
            if (isEmpty(this.brandUrls)) return false;
            const filteredBrandUrls = this.brandUrls.filter(brand => URBN_BRANDS.includes(brand.displayName));
            if (isEmpty(filteredBrandUrls)) return false;
            return filteredBrandUrls[0];
        },
        msrpValue() {
            const msrp = get(this.productDetails, 'msrpValue', 0);
            if (!msrp) {
                return false;
            }
            return this.$filters.i18nFormatDollarsNoCents(msrp);
        },
        reviewCount() {
            return get(this.productDetails, 'reviewSummary.overallRatingCount', 0);
        },
        overallRatingAverage() {
            return get(this.productDetails, 'reviewSummary.overallRatingAverage', 0);
        },
        stars() {
            const stars = [];

            for (let index = 0; index < MAX_RATING; index++) {
                // Because JS uses floating point for fractional numbers, the remainder comes out to 0.899 (repeating)
                // when the average rating in x.9, which causes a half star to be incorrectly rendered. Number.toFixed()
                // rounds a decimal so that it can be accurately compared
                const remainder = (this.overallRatingAverage - index).toFixed(2);
                const colorIsPrimary = remainder >= 0.2;

                // the requirement is to have a threshold of -0.1 / +0.2 to display a wholly-filled star.
                // e.g. a rating from 2.9 - 3.19 should show as three stars, 3.2 - 3.9 as three and a half stars
                // and 3.9 - 4.19 as four stars
                const icon = (colorIsPrimary && remainder < 0.9) ? 'global--star-half' : 'global--star';

                stars.push({
                    color: colorIsPrimary ? 'primary' : 'medium-gray',
                    icon,
                });
            }

            return stars;
        },
        toastDescription() {
            return `${this.$t('closet__savedToCloset--confirmation')} '${this.productDetails.displayName}'`;
        },
        toastImages() {
            return [].concat(this.firstProductImage).filter(val => !!val);
        },
        popoverOffsets() {
            return {
                x: 0,
                y: 8,
            };
        },
    },
};
</script>

<style lang="scss">
    .c-product-info-header {
        &__rating-price-container{
            display: flex;
            justify-content: space-between;
            color: $nu-gray;
            align-items: center;
        }

        &__rating-star {
            margin-right: $nu-spacer-0pt5;

            &--disabled {
                cursor: default;
            }

            &:nth-of-type(5) {
                margin-right: $nu-spacer-1;
            }

        }

        &__review-count {
            vertical-align: top;
        }

        &__msrp {
            margin-left: $nu-spacer-1;
        }

    }
</style>
